import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: string;
  height?: string;
  padding?: string;
  containerPadding?: string;
  id?: string;
  className?: string;
  hideCloseButton?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKey?: boolean;
  disableCloseButton?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = 'w-[500px]',
  height = '',
  padding = 'p-6',
  containerPadding = 'p-4',
  id,
  className = '',
  hideCloseButton = false,
  disableBackdropClick = false,
  disableEscapeKey = false,
  disableCloseButton = false
}) => {
  // Handle ESC key press and body scroll lock
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !disableEscapeKey) {
        onClose();
      }
    };

    // Add event listener for ESC key
    document.addEventListener('keydown', handleEscKey);

    // Handle body scroll lock
    if (isOpen) {
      // Store current scroll position and body padding
      const scrollY = window.scrollY;
      const paddingRight = window.innerWidth - document.documentElement.clientWidth;

      // Detect iOS
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      // Add styles to prevent scroll
      if (!isIOS) {
        // Non-iOS: use position fixed
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        document.body.style.paddingRight = `${paddingRight}px`;
      } else {
        // iOS: use overflow hidden (better keyboard handling)
        document.body.style.overflow = 'hidden';
        document.body.style.height = '100vh';
        document.body.style.touchAction = 'none';
      }
    }

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      
      // Restore body scroll when dialog closes
      if (isOpen) {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (!isIOS) {
          const scrollY = document.body.style.top;
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.style.paddingRight = '';
          window.scrollTo(0, parseInt(scrollY || '0') * -1);
        } else {
          document.body.style.overflow = '';
          document.body.style.height = '';
          document.body.style.touchAction = '';
        }
      }
    };
  }, [isOpen, onClose, disableEscapeKey]);

  if (!isOpen) return null;

  const dialogContent = (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-300 ease-out ${className}`}
      data-testid={`${id}-overlay`}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-out
          ${isOpen ? 'bg-opacity-50' : 'bg-opacity-0'}`}
        onClick={disableBackdropClick ? undefined : onClose}
        data-testid={`${id}-backdrop`}
      />

      {/* Dialog */}
      <div className={`flex min-h-[100vh] items-center justify-center ${containerPadding}`}>
        <div
          className={`relative bg-white rounded-lg shadow-xl ${width} ${height} ${padding}
            transform transition-all duration-300 ease-out max-h-[90vh] overflow-y-auto
            ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${id}-content`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Close button */}
          {!hideCloseButton && (
            <button
              onClick={onClose}
              className={`absolute right-4 top-4 p-2 rounded-full z-[99] ${
                disableCloseButton 
                  ? 'cursor-not-allowed text-gray-300' 
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
              data-testid={`${id}-close-button`}
              disabled={disableCloseButton}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          )}

          {/* Title */}
          {title && (
            <div className="mb-2">
              {/* <h2 className="text-xl font-semibold" data-testid={`${id}-title`}>{title}</h2> */}
              <h2 className="text-2xl font-bold" data-testid={`${id}-title`}>{title}</h2>

            </div>
          )}

          {/* Content */}
          <div data-testid={`${id}-body`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // Use createPortal to append the dialog to the body
  return createPortal(dialogContent, document.body);
};

export default Dialog; 